// deps
import { useCallback, useContext, useEffect, useMemo } from "react";

// internal-deps
import {
    AnalyticsContext,
    CartOverlaysContext,
    getBridgeSdk,
    NEWRELIC_TABLES,
    recordNREvent,
} from "@swiggy-private/checkout-core";

// types
import { CroutonIds, CroutonPriority } from "@swiggy-private/cart-types";
import { ICrazyDealsExpiryCrouton } from "./types";
import { RefreshScreenLinkTypes, RefreshTypes } from "@swiggy-private/native-bridge";

// helpers
import { isEmpty } from "@swiggy-private/core-ui";

// styles
import styles from "./useCrazyDealsExpiryCrouton.scss";

//context
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CartContext } from "@cart-client/contexts/CartContext";
import { croutonImpressionEvent } from "@cart-client/analytics/events/CrazyDealsExpiryCrouton";

const useCrazyDealsExpiryCrouton = (props: ICrazyDealsExpiryCrouton) => {
    const { setCroutonVisibility, canShowTimer } = props;

    const { registerCrouton } = useContext(CartOverlaysContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;

    const { reloadCart } = useContext(UpdateCartContext);
    const onCompleteTimer = useCallback(() => {
        reloadCart();
        recordNREvent({ eventType: NEWRELIC_TABLES.CRAZY_DEALS_TIMER_END_UPDATE_CART_CALLED });
    }, [reloadCart]);

    const croutonExpiredData = cartResponse?.data?.flash_deal_expiry_toast;
    const showTimer = cartResponse?.statusCode === 0 && cartResponse?.data?.flash_deals_timer?.show_expiry_timer;
    const isRefreshMenu = cartResponse?.data?.refresh_menu_flag;
    const restaurantId = cartResponse?.data?.restaurant_details?.id;
    const crazyDealsCroutonData = cartResponse?.data?.flash_deals_timer;

    const croutonDuration = useMemo(() => Math.round((crazyDealsCroutonData?.expiry_time ?? 0) / 1000), [
        crazyDealsCroutonData?.expiry_time,
    ]);

    useEffect(() => {
        if (!isEmpty(isRefreshMenu)) {
            const restaurantIdValue = typeof restaurantId === "string" ? restaurantId : restaurantId?.toString();
            getBridgeSdk().refreshScreen({
                type: RefreshTypes.SCREEN,
                link: RefreshScreenLinkTypes.MENU,
                params: {
                    restaurantId: restaurantIdValue || "",
                },
            });
        }
    }, [isRefreshMenu, restaurantId]);

    useEffect(() => {
        if (canShowTimer) {
            return;
        }

        let payload;
        if (!isEmpty(croutonExpiredData)) {
            const expiredMessage = croutonExpiredData?.message || "";
            croutonImpressionEvent(
                JSON.stringify({
                    msg: `${expiredMessage}`,
                }),
                impressionEvent,
            );
            payload = {
                id: CroutonIds.CRAZY_DEAL_EXPIRED,
                priority: CroutonPriority.PRIORITY_ORDER_TWO,
                content: {
                    title: expiredMessage,
                    icon: croutonExpiredData?.logo,
                    setCroutonVisibility,
                    iconClassName: styles.crazyDealsCroutonIcon,
                    titleClassName: styles.crazyDealsCroutonText,
                    buttonClassName: styles.crazyDealsCroutonBtn,
                    durationOfCrouton: croutonExpiredData?.crouton_timeout,
                },
            };
        } else if (!isEmpty(crazyDealsCroutonData) && !isEmpty(croutonDuration)) {
            const message = crazyDealsCroutonData?.placeholder_message || "";
            croutonImpressionEvent(
                JSON.stringify({
                    msg: `${message} ${croutonDuration}`,
                }),
                impressionEvent,
            );
            payload = {
                id: CroutonIds.CRAZY_DEAL_EXPIRY_TIMER,
                priority: CroutonPriority.PRIORITY_ORDER_TWO,
                content: {
                    title: message,
                    icon: crazyDealsCroutonData?.logo || "",
                    showTimer,
                    duration: croutonDuration,
                    onCompleteTimer,
                    setCroutonVisibility,
                    iconClassName: styles.crazyDealsCroutonIcon,
                    titleClassName: styles.crazyDealsCroutonText,
                    buttonClassName: styles.crazyDealsCroutonBtn,
                },
            };
        }

        if (payload) {
            registerCrouton(payload);
        }
    }, [
        canShowTimer,
        croutonExpiredData,
        croutonDuration,
        onCompleteTimer,
        registerCrouton,
        setCroutonVisibility,
        showTimer,
        impressionEvent,
        crazyDealsCroutonData,
    ]);
};

export default useCrazyDealsExpiryCrouton;
